// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FiTrash2 } from "react-icons/fi";

import { ReactComponent as HomeSmile } from "../assets/icons/home-smile.svg";
import { ReactComponent as LightningIcon } from "../assets/icons/lightning.svg";
import { ReactComponent as LineChartIcon } from "../assets/icons/line-chart.svg";

import { useImagePreloader } from "../hooks/useImagePreloader";

import { setModalState, modalViewsConst } from "../store/modalSlice";
import { setFlyLocation } from "../store/mapStateSlice";
import { assetSchema } from "../store/assetsSlice";
import { schemaMatch } from "../util/utils";
import { imageCache } from "../utils/imageCache";
import { Skeleton, SkeletonText } from "./Skeleton";
import bgpattern from "../assets/images/bg-pattern-01.svg";

// Card action row, built like the Model Garden card's: icon buttons, then the Edit text pill
const CARD_ACTIONS = [
  {
    id: "delete",
    label: "Delete",
    content: <FiTrash2 size={12} />,
    buttonClass:
      "eai-btn-icon eai-btn-icon-danger eai-card-reveal",
    handlerKey: "delete",
    disabledTitle: "Cannot delete assets in organization-owned groups",
  },
  {
    id: "energy-model",
    label: "Energy Model",
    content: <LightningIcon className="h-3.5 w-3.5 [&_path]:stroke-current" />,
    buttonClass: "eai-btn-icon",
    handlerKey: "curveEdit",
    disabledTitle: "Cannot modify energy model for assets in organization-owned groups",
  },
  {
    id: "custom-forecast",
    label: "Add Custom Forecast",
    content: <LineChartIcon className="h-3.5 w-3.5 [&_path]:stroke-current" />,
    buttonClass: "eai-btn-icon",
    handlerKey: "customForecast",
    disabledTitle: "Cannot add custom forecast for assets in organization-owned groups",
    adminOnly: true,
  },
  {
    id: "edit",
    label: "Edit Asset",
    content: "Edit",
    buttonClass: "eai-btn-outline-xs",
    handlerKey: "edit",
    disabledTitle: "Cannot edit assets in organization-owned groups",
  },
];

interface AssetCardProps {
  asset: any;
  organizationId?: string;
  groupOwner?: string;
  currentUserId?: string;
  isAdmin?: boolean;
}

// Same rail card as the group tiles — image rail left, copy right
export const AssetCard = ({
  asset,
  organizationId,
  groupOwner,
  currentUserId,
  isAdmin,
}: AssetCardProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const isGroupOwner = currentUserId && groupOwner === currentUserId;

  // Assets in an organization group are only editable by an admin or the group owner
  const isActionDisabled = organizationId && !isAdmin && !isGroupOwner;

  useEffect(() => {
    let cancelled = false;

    const fetchBackgroundImage = async () => {
      if (!asset?.imageUrl) return;
      try {
        const cachedUrl = await imageCache.getSignedUrl(asset.imageUrl);
        if (!cancelled) setBackgroundUrl(cachedUrl || null);
      } catch (error) {
        console.error(`AssetCard: could not load image for ${asset?.name}:`, error);
        if (!cancelled) setBackgroundUrl(null);
      }
    };

    setBackgroundUrl(null);
    fetchBackgroundImage();
    return () => {
      cancelled = true;
    };
  }, [asset?.imageUrl, asset?.name]);

  // __modalSize is what tells ModalGlobal a modal is on ModalBase — without it the frame is the legacy one
  const openModal = (modalView, properties, size = "md") => (e) => {
    e.stopPropagation();
    if (isActionDisabled) return;

    dispatch(setModalState({ modalOpen: true, modalView, properties: { ...properties, __modalSize: size } }));
  };

  const handlers = {
    delete: openModal(modalViewsConst.deleteAssetModal, asset, "sm"),
    edit: openModal(modalViewsConst.editAssetNEW, schemaMatch(asset, assetSchema)),
    curveEdit: openModal(modalViewsConst.assetPowerCurveEdit, asset),
    customForecast: openModal(modalViewsConst.customForecastEdit, asset),
  };

  const handleCardClick = () => {
    navigate(`/home/group/${asset.assetGroupId}/asset/${asset.id}`);
    dispatch(setFlyLocation([asset.lng, asset.lat]));
  };

  return (
    <div
      onClick={handleCardClick}
      data-testid="asset-card"
      className="eai-rail-card group cursor-pointer relative w-full min-w-0"
    >
      <div
        className="eai-rail bg-eai-surface bg-center"
        style={{
          backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : `url(${bgpattern})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        {/* Without an asset picture the rail is just the pattern — badge it so the card still reads as an asset */}
        {!backgroundUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-eai-border bg-eai-surface-card shadow-eai-subtle">
              <HomeSmile className="h-5 w-5" />
            </div>
          </div>
        )}
      </div>

      <div className="eai-rail-copy">
        <div className="flex items-start gap-2">
          {/* Reserved to two lines, like every other rail card, so the description starts on one y */}
          <h3 className="eai-card-title min-w-0 flex-1 line-clamp-2 break-words min-h-[2.4em]" title={asset.name}>
            {asset.name}
          </h3>
          <span className="shrink-0 text-sm text-eai-ink">→</span>
        </div>

        {/* Reserved either way, so the action row lands on the same y across every card */}
        <p className="eai-card-description">{asset.description}</p>

        {/* Actions live on the card — stop the clicks reaching the card's navigation */}
        <div className="mt-auto flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {CARD_ACTIONS.filter((action) => !action.adminOnly || isAdmin).map(
            ({ id, label, content, buttonClass, handlerKey, disabledTitle }) => (
              <button
                key={id}
                type="button"
                onClick={handlers[handlerKey]}
                disabled={isActionDisabled}
                data-testid={`asset-card-${id}`}
                aria-label={label}
                title={isActionDisabled ? disabledTitle : label}
                className={`${buttonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {content}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// Placeholder matching AssetCard's layout — keep the two in sync when the card changes.
export const AssetCardSkeleton = () => (
  <div className="eai-rail-card h-full w-full min-w-0">
    <Skeleton className="eai-rail" radius="none" />
    <div className="eai-rail-copy">
      <SkeletonText lines={2} lineClassName="h-3.5" />
      {/* Same reserved height as the card's description, so nothing shifts when the asset lands */}
      <div className="min-h-[2.9em]">
        <SkeletonText lines={2} lineClassName="h-3" />
      </div>
      <div className="mt-auto flex items-center justify-end gap-1.5">
        <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
        <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
        <Skeleton className="h-eai-control-xs w-12" radius="full" />
      </div>
    </div>
  </div>
);

export const AssetGrid = ({ assetItems, organizationId, groupOwner, currentUserId, isAdmin }) => {
  const assets = assetItems ?? [];

  // Warm the image cache so the rails don't pop in one by one
  useImagePreloader(assets);

  if (assets.length === 0) {
    return (
      <div className="eai-description-sm py-10 text-center">
        No assets in this group yet.
      </div>
    );
  }

  return (
    <div className="eai-card-grid">
      {assets.map((asset) => (
        <AssetCard
          key={asset.id}
          asset={asset}
          organizationId={organizationId}
          groupOwner={groupOwner}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
};
